// src/shared/services/authService.ts

import { refreshAuthToken, SessionExpiredError } from '../../config/api';

// Re-export so consumers can import all session helpers from one place.
export { refreshAuthToken, SessionExpiredError };

interface JwtPayload {
  exp?: number;
  [claim: string]: unknown;
}

/**
 * Decode a JWT payload WITHOUT verifying the signature. This is client-side
 * only and used purely to read the `exp` claim so the UI can warn the user
 * before the session lapses — the backend remains the sole source of truth for
 * authorization. Returns null when the token is missing or malformed.
 */
export function decodeJwt(token: string | null): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Epoch-millis expiry of the stored auth token, or null when there is no
 * readable `exp` (missing/malformed token).
 */
export function getTokenExpiryMs(
  token: string | null = localStorage.getItem('authToken'),
): number | null {
  const payload = decodeJwt(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

/**
 * Milliseconds until the stored token expires. Negative when it has already
 * expired; null when there is no readable expiry.
 */
export function getMsUntilExpiry(): number | null {
  const expMs = getTokenExpiryMs();
  return expMs === null ? null : expMs - Date.now();
}
